import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Clock, MapPin, Search, Sparkles, Star } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { experienceApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { asArray } from "@/lib/asArray";
import type { Experience } from "@/types";

const HERO_IMG =
  "https://images.unsplash.com/photo-1599661046289-e31897846e41?auto=format&fit=crop&w=2000&q=80";
const SAVED_KEY = "maayad_exp_saved";

/* saved-for-later (client-side) */
function useSaved() {
  const [ids, setIds] = useState<Set<string>>(() => {
    try {
      return new Set<string>(JSON.parse(localStorage.getItem(SAVED_KEY) || "[]"));
    } catch {
      return new Set<string>();
    }
  });
  const toggle = (id: string) =>
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem(SAVED_KEY, JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  return { ids, toggle };
}

/* time-of-day helpers */
function greetingWord(h: number) {
  return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
}
function slotOf(h: number): "morning" | "afternoon" | "evening" | "night" {
  return h < 12 ? "morning" : h < 17 ? "afternoon" : h < 21 ? "evening" : "night";
}
// ordered by priority for "right now" — afternoon nudges toward sunset/evening
const RELEVANT: Record<string, string[]> = {
  morning: ["morning", "anytime", "afternoon"],
  afternoon: ["evening", "afternoon", "anytime"],
  evening: ["evening", "night", "anytime"],
  night: ["night", "evening", "anytime"],
};
const SLOT_SUBTITLE: Record<string, string> = {
  morning: "Good morning — ease into the day with these picks.",
  afternoon: "It's afternoon — perfect time to lock in a sunset or evening plan.",
  evening: "The city's glowing right now — here's what's great this evening.",
  night: "Late night in Jaipur — these are still going strong.",
};

function fmtDuration(m: number) {
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h && min) return `${h}h ${min}m`;
  if (h) return `${h} hr${h > 1 ? "s" : ""}`;
  return `${m} min`;
}
const money = (n: number) => `₹${n.toLocaleString("en-IN")}`;

const CHIPS = ["Today", "Under ₹1000", "Near Me (5km)", "Family Friendly"] as const;
type Chip = (typeof CHIPS)[number];

/* -------------------------------- page ---------------------------------- */
export default function Experiences() {
  const { user } = useAuth();
  const saved = useSaved();
  const [items, setItems] = useState<Experience[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<Set<Chip>>(new Set());

  const now = new Date();
  const hour = now.getHours();
  const slot = slotOf(hour);
  const firstName = user?.full_name?.split(" ")[0] ?? "explorer";

  useEffect(() => {
    experienceApi
      .list()
      .then((r) => setItems(asArray<Experience>(r.data)))
      .catch((e) => {
        setError(errorMessage(e, "Could not load experiences"));
        setItems([]);
      });
  }, []);

  const toggleChip = (c: Chip) =>
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  const filtered = useMemo(() => {
    let list = items ?? [];
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((e) =>
        `${e.title} ${e.category} ${e.vibe} ${e.description}`.toLowerCase().includes(q),
      );
    }
    if (active.has("Today")) list = list.filter((e) => RELEVANT[slot].includes(e.best_time));
    if (active.has("Under ₹1000")) list = list.filter((e) => e.price < 1000);
    if (active.has("Near Me (5km)")) list = list.filter((e) => e.distance_km <= 5);
    if (active.has("Family Friendly"))
      list = list.filter((e) => e.tags.includes("Family Friendly") || e.vibe === "Family");
    return list;
  }, [items, query, active, slot]);

  const isFiltering = query.trim().length > 0 || active.size > 0;

  // swimlanes (only when not actively filtering)
  const all = items ?? [];
  const rightNow = [...all]
    .filter((e) => RELEVANT[slot].includes(e.best_time))
    .sort((a, b) => RELEVANT[slot].indexOf(a.best_time) - RELEVANT[slot].indexOf(b.best_time));
  const aiSuggests = [...all].sort((a, b) => b.rating - a.rating).slice(0, 8);
  const trending = [...all].sort((a, b) => b.reviews_count - a.reviews_count).slice(0, 8);
  const byVibe = (v: string) => all.filter((e) => e.vibe === v);

  return (
    <div className="min-h-screen bg-cream">
      <Navbar overlay />

      {/* Contextual hero */}
      <section className="relative w-full overflow-hidden">
        <img src={HERO_IMG} alt="Jaipur" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/90 via-navy-900/80 to-navy-900/95" />
        <div className="container-page relative pt-28 pb-10">
          <p className="text-sm font-medium text-gold-400">
            {now.toLocaleDateString("en-IN", { weekday: "long" })} ·{" "}
            {now.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })} · Jaipur
          </p>
          <h1 className="mt-2 max-w-3xl font-display text-3xl font-bold text-white sm:text-4xl">
            Good {greetingWord(hour)}, {firstName}. Looking for something to do in Jaipur today?
          </h1>

          {/* Smart search */}
          <div className="mt-6 flex max-w-2xl items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 backdrop-blur-xl">
            <Search size={18} className="text-white/60" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find heritage walks, pottery classes, or local food tours"
              className="w-full bg-transparent py-1.5 text-sm text-white placeholder:text-white/50 focus:outline-none"
            />
          </div>

          {/* Quick filter chips */}
          <div className="mt-4 flex flex-wrap gap-2">
            {CHIPS.map((c) => {
              const on = active.has(c);
              return (
                <button
                  key={c}
                  onClick={() => toggleChip(c)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
                    on
                      ? "border-gold-400 bg-gold-400 text-navy-900"
                      : "border-white/25 text-white/90 hover:bg-white/10"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <main className="container-page py-10">
        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {items === null ? (
          <div className="flex gap-4 overflow-hidden">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-72 w-72 shrink-0 animate-pulse rounded-2xl bg-white" />
            ))}
          </div>
        ) : isFiltering ? (
          /* -------- filtered grid -------- */
          <>
            <h2 className="mb-4 font-display text-xl font-bold text-navy-900">
              {filtered.length} experience{filtered.length === 1 ? "" : "s"}
              {active.has("Today") ? " for today" : ""}
            </h2>
            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center">
                <p className="font-display text-lg font-semibold text-navy-900">Nothing matches yet</p>
                <p className="mt-1 text-sm text-slate-500">Try clearing a filter or searching something else.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filtered.map((e) => (
                  <ExperienceCard key={e.public_id} exp={e} saved={saved.ids.has(e.public_id)} onSave={() => saved.toggle(e.public_id)} />
                ))}
              </div>
            )}
          </>
        ) : (
          /* -------- swimlanes -------- */
          <div className="space-y-10">
            <Swimlane title="Good for right now" subtitle={SLOT_SUBTITLE[slot]} items={rightNow} saved={saved} />
            <Swimlane
              title="Maayad AI Suggests"
              subtitle="Personalised picks we think you'll love."
              icon={<Sparkles size={18} className="text-iris-500" />}
              items={aiSuggests}
              saved={saved}
            />
            <Swimlane title="Trending in Jaipur" subtitle="Most booked by travellers this week." items={trending} saved={saved} />
            <Swimlane title="Hidden Local Gems" items={byVibe("Hidden Gems")} saved={saved} />
            <Swimlane title="Late Night Bites" items={byVibe("Late Night Bites")} saved={saved} />
            <Swimlane title="Golden Hour" items={byVibe("Golden Hour")} saved={saved} />
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

/* ------------------------------- swimlane -------------------------------- */
function Swimlane({
  title,
  subtitle,
  icon,
  items,
  saved,
}: {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  items: Experience[];
  saved: { ids: Set<string>; toggle: (id: string) => void };
}) {
  if (items.length === 0) return null;
  return (
    <section>
      <div className="mb-3">
        <h2 className="flex items-center gap-2 font-display text-xl font-bold text-navy-900">
          {icon} {title}
        </h2>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
      <div className="-mx-1 flex snap-x gap-4 overflow-x-auto px-1 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((e) => (
          <div key={e.public_id} className="w-72 shrink-0 snap-start">
            <ExperienceCard exp={e} saved={saved.ids.has(e.public_id)} onSave={() => saved.toggle(e.public_id)} />
          </div>
        ))}
      </div>
    </section>
  );
}

/* --------------------------- experience card ----------------------------- */
function ExperienceCard({ exp, saved, onSave }: { exp: Experience; saved: boolean; onSave: () => void }) {
  const discounted = exp.original_price && exp.original_price > exp.price;
  return (
    <Link
      to={`/experiences/${exp.public_id}`}
      className="group block overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card transition duration-300 hover:-translate-y-1 hover:shadow-lift"
    >
      <div className="relative h-40 w-full overflow-hidden bg-slate-100">
        {exp.image_url ? (
          <img src={exp.image_url} alt={exp.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="grid h-full place-items-center text-sm text-slate-300">No image</div>
        )}
        {/* tags */}
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          {exp.tags.slice(0, 2).map((t) => (
            <span key={t} className="rounded-full bg-navy-900/80 px-2 py-0.5 text-[11px] font-semibold text-gold-400 backdrop-blur">
              {t}
            </span>
          ))}
        </div>
        {/* save */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSave();
          }}
          aria-label={saved ? "Remove from saved" : "Save for later"}
          className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-white/90 shadow backdrop-blur transition hover:scale-110"
        >
          <Bookmark size={16} className={saved ? "fill-iris-500 text-iris-500" : "text-navy-700"} />
        </button>
      </div>

      <div className="p-3.5">
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-1 font-semibold text-navy-800">
            <Star size={13} className="fill-gold-500 text-gold-500" />
            {exp.rating.toFixed(1)} <span className="font-normal text-slate-400">({exp.reviews_count})</span>
          </span>
          <span className="text-slate-400">{exp.category}</span>
        </div>
        <h3 className="mt-1 line-clamp-2 font-display text-base font-semibold leading-snug text-navy-900">{exp.title}</h3>
        <div className="mt-1.5 flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><Clock size={12} /> {fmtDuration(exp.duration_minutes)}</span>
          <span className="flex items-center gap-1"><MapPin size={12} className="text-iris-500" /> {exp.distance_km.toFixed(1)} km</span>
        </div>
        <div className="mt-3 flex items-end gap-2">
          <span className="font-display text-lg font-bold text-navy-900">{money(exp.price)}</span>
          {discounted && <span className="mb-0.5 text-xs text-slate-400 line-through">{money(exp.original_price as number)}</span>}
        </div>
      </div>
    </Link>
  );
}